import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { confirm } from 'actions/app';
import { hideReferenceReleaseComposer } from 'actions/joborder';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { JOB_ORDER_DRAWERS, JOB_ORDER_PROFILE_CONFIG } from 'features/inventory-profiles/constants';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import {
  canUserOrStaffEditEntity,
  Permissions,
  userHasPermission,
  userHasRoles
} from 'services/Authorization';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { completeReferenceRelease, removeQueryParams, sendoutDataToForm } from 'UI/utils/inventory';

const COPIES = strings.inventoryProfiles.sections.jobOrders;

const DUPLICATE_ERROR_AUTO_HIDE_MS = 5000;

/**
 * What the current user may see or do on a Job Order profile, and whether its company is signed
 * (it decides between the sendout form and the notice).
 * @param {Object} options
 * @param {Object} options.jobOrder Job order profile
 * @param {boolean} [options.isAlreadyAccountable] The user is already the recruiter: nothing to claim
 * @returns {Object} Named booleans
 */
export const useJobOrderPermissions = ({ jobOrder, isAlreadyAccountable = false }) => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const canUseSendouts = userHasPermission(Permissions.Sendouts.CanUseModule);

  return {
    canClaimAccountability:
      checkIfFeatureFlagEnabled(FeatureFlags.ClaimAccountabilityJobOrder) && !isAlreadyAccountable,
    canEdit: canUserOrStaffEditEntity(user, jobOrder),
    canSeeJobDescription: checkIfFeatureFlagEnabled(FeatureFlags.JobOrderJobDescription),
    canSeePlacements: userHasPermission(Permissions.Placements.CanUseModule),
    canSeeSendoutsV2: canUseSendouts && checkIfFeatureFlagEnabled(FeatureFlags.SendoutsV2),
    canUseDailyPlan:
      checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTaskFromProfile) &&
      userHasPermission(Permissions.DailyPlan.CanUseModule),
    canUsePresentationMaker: userHasPermission(Permissions.Inventory.PresentationMaker),
    canUseRoleplay:
      checkIfFeatureFlagEnabled(FeatureFlags.AIRoleplay) &&
      userHasPermission(Permissions.AIIntegrations.Roleplay),
    canUseSendouts,
    canVerifyUrgency:
      checkIfFeatureFlagEnabled(FeatureFlags.UrgencyVerification) &&
      userHasRoles([Roles.RegionalDirector, Roles.AssistantRegionalDirector, Roles.Leadership]),
    isCompanySigned: jobOrder.company?.type?.id !== CompanyType.NotSigned
  };
};

/**
 * Commands of the Job Order profile: duplicate, navigation, the sendout the form edits, the
 * placement drawer with its preselected sendout and the reference release composer, which stays
 * in Redux because shared placement components open it.
 * @param {Object} params
 * @param {number} params.jobOrderId `jobOrder.id`
 * @param {string} params.jobOrderTitle `jobOrder.title`
 * @param {number} [params.companyId] `jobOrder.company.id`
 * @param {number|string|null} params.sendoutId `drawerItemId` of useProfileDrawer: the clicked sendout, null for a new one
 * @param {boolean} params.isCompanySigned `permissions.isCompanySigned`
 * @param {(name: string) => (item: { id: number|string }) => void} params.openItemDrawer From useProfileDrawer
 */
export const useJobOrderActions = ({
  companyId,
  isCompanySigned,
  jobOrderId,
  jobOrderTitle,
  openItemDrawer,
  sendoutId
}) => {
  const dispatch = useDispatch();
  const { showAlert } = useShowAlert();
  const history = useHistory();
  const location = useLocation();
  const { referenceRelease } = useSelector(state => state.jobOrder);

  const { state: sendoutState } = useFetchWithStatusV2({
    endpoint: sendoutId ? `${Endpoints.Sendouts}/${sendoutId}` : null,
    initialState: {}
  });
  const isSelectedSendoutLoaded = Boolean(sendoutId) && sendoutState.results?.id === sendoutId;
  const selectedSendout = isSelectedSendoutLoaded
    ? sendoutDataToForm(sendoutState.results, { id: jobOrderId, title: jobOrderTitle })
    : null;

  const duplicate = () =>
    dispatch(
      confirm({
        severity: 'warning',
        ...COPIES.duplicateDialog,
        cancelButtonText: strings.shared.ui.cancel,
        onConfirm: async ok => {
          if (!ok) return;

          try {
            const response = await API.post(
              `${JOB_ORDER_PROFILE_CONFIG.endpoint}/${jobOrderId}/duplicate`
            );
            if (!response.data || response.status !== HTTPStatusCodes.Created) return;

            showAlert({ title: COPIES.successDuplication });
            history.push(JOB_ORDER_PROFILE_CONFIG.route(response.data.data.id));
          } catch (error) {
            showAlert({
              title: JOB_ORDER_PROFILE_CONFIG.sectionName,
              error,
              autoHideDuration: DUPLICATE_ERROR_AUTO_HIDE_MS
            });
          }
        }
      })
    );

  const openDataSheet = () => history.push(EntityRoutes.JobOrderEdit.replace(':id', jobOrderId));

  const sendoutDrawer = isCompanySigned
    ? JOB_ORDER_DRAWERS.Sendout
    : JOB_ORDER_DRAWERS.SendoutUnavailable;

  const openNewPlacement = ({ sendoutId: preselectedSendoutId }) =>
    openItemDrawer(JOB_ORDER_DRAWERS.Placement)({
      id: preselectedSendoutId ? parseInt(preselectedSendoutId, 10) : null
    });

  const navigateFromSendout = (event, options) =>
    openItemDrawer(JOB_ORDER_DRAWERS.Placement)({ id: options?.sendoutId });

  const clearPlacementQuery = () =>
    history.replace({
      search: removeQueryParams(location.search, ['form', 'id']),
      hash: location.hash
    });

  const goToCompanyProfile = () =>
    history.push(EntityRoutes.CompanyProfile.replace(':id', companyId));

  const closeReferenceRelease = () => dispatch(hideReferenceReleaseComposer());
  const { handleReferenceReleaseComplete } = completeReferenceRelease({
    referenceRelease,
    referenceReleaseHandler: closeReferenceRelease
  });

  return {
    clearPlacementQuery,
    closeReferenceRelease,
    completeReferenceRelease: handleReferenceReleaseComplete,
    duplicate,
    goToCompanyProfile,
    isSendoutFormReady: !sendoutId || isSelectedSendoutLoaded,
    navigateFromSendout,
    openDailyPlan: () => history.push(EntityRoutes.DailyPlan),
    openDataSheet,
    openNewPlacement,
    referenceReleaseCandidateId: referenceRelease?.candidateId,
    selectedSendout,
    sendoutDrawer
  };
};
